import { AzureEmbeddingSettings, EmbeddingProvider } from "@/types/EmbeddingSettings";
import AzureEmbeddingSettingsForm from "./AzureEmbeddingSettingsForm";
import { forwardRef, useState, useImperativeHandle, useRef, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OllamaEmbeddingSettingsForm from "./OllamaEmbeddingSettingsForm";
import JunetEmbeddingSettingsForm from "./JunetEmbeddingSettingsForm";

type Props = {
    data?: AzureEmbeddingSettings;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

const EMBEDDING: { label: string; value: EmbeddingProvider; image: string }[] = [
    {
        label: "Azure",
        value: "azure",
        image: "/assets/providers/azure.svg",
    },
    {
        label: "Ollama",
        value: "ollama",
        image: "/assets/providers/ollama.svg",
    },
    {
        label: "Junet Core",
        value: "junet",
        image: "/favicon.png",
    },
];

const EmbeddingSettingsForm = forwardRef(({ data, initialData = {}, onDataChange, onSubmit, ...props }: Props, ref) => {
    const [embeddingProvider, setEmbeddingProvider] = useState<EmbeddingProvider | string>(
        initialData?.EMBEDDING_PROVIDER || data?.EMBEDDING_PROVIDER || "azure"
    );

    const formRef: any = useRef(null);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        validateForm: async () => {
            if (formRef.current && formRef.current.validateForm) {
                return await formRef.current.validateForm();
            }
            return true;
        },
        getFormData: () => {
            const displayNameInput = document.getElementById("display-name-embedding") as HTMLInputElement;
            const providerData = {
                EMBEDDING_PROVIDER: embeddingProvider,
                display_name: displayNameInput?.value || "",
            };
            if (formRef.current && formRef.current.getFormData) {
                return {
                    ...providerData,
                    ...formRef.current.getFormData(),
                };
            }
            return providerData;
        },
        submitForm: () => {
            if (formRef.current && formRef.current.submitForm && onSubmit) {
                formRef.current.submitForm();
            }
        },
    }));

    // Update parent component when embedding provider changes
    useEffect(() => {
        if (onDataChange) {
            onDataChange({
                ...initialData,
                EMBEDDING_PROVIDER: embeddingProvider,
            });
        }
    }, [embeddingProvider]); // Remove initialData and onDataChange from dependencies

    const handleDataChange = (formData: any) => {
        if (onDataChange) {
            const displayNameInput = document.getElementById("display-name-embedding") as HTMLInputElement;
            onDataChange({
                ...formData,
                EMBEDDING_PROVIDER: embeddingProvider,
                display_name: displayNameInput?.value || "",
            });
        }
    };

    const handleSubmit = (_data: any) => {
        if (!onSubmit) return;

        const displayNameInput = document.getElementById("display-name-embedding") as HTMLInputElement;
        const __data = {
            ...data,
            ..._data,
            EMBEDDING_PROVIDER: embeddingProvider,
            display_name: displayNameInput?.value || "",
        };

        onSubmit(__data);
    };

    const renderForm = (embeddingProvider: EmbeddingProvider | string) => {
        const formProps = {
            ...props,
            onSubmit: handleSubmit,
            data: {
                ...data,
                ...initialData,
            },
            onDataChange: handleDataChange,
        };

        switch (embeddingProvider) {
            case "azure":
                return <AzureEmbeddingSettingsForm {...formProps} ref={formRef} />;
            case "ollama":
                return <OllamaEmbeddingSettingsForm {...formProps} ref={formRef} />;
            case "junet":
                return <JunetEmbeddingSettingsForm {...formProps} ref={formRef} />;
            default:
                return <AzureEmbeddingSettingsForm {...formProps} ref={formRef} />;
        }
    };

    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="display-name-embedding">Display Name</Label>
                <Input
                    id="display-name-embedding"
                    type="text"
                    defaultValue={initialData?.display_name || ""}
                    onChange={(e) => {
                        if (onDataChange) {
                            onDataChange({
                                ...initialData,
                                display_name: e.target.value,
                                EMBEDDING_PROVIDER: embeddingProvider,
                            });
                        }
                    }}
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="embedding-provider">Provider</Label>
                <Select defaultValue={embeddingProvider} onValueChange={setEmbeddingProvider}>
                    <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select Embedding" id="embedding-provider" />
                    </SelectTrigger>
                    <SelectContent>
                        {EMBEDDING.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                                <div className="flex items-center gap-2">
                                    <img src={item.image} alt={item.label} width="20" height="20" />
                                    {item.label}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            {renderForm(embeddingProvider)}
        </div>
    );
});

EmbeddingSettingsForm.displayName = "EmbeddingSettingsForm";

export default EmbeddingSettingsForm;
